/**
 * The mark, building itself, for the moments the app has nothing to show yet.
 *
 * Both full-screen waits — signing in, and a protected route resolving a
 * session — showed the word AFELL fading in and out. That reads as a page that
 * has stopped rather than one that is working. Todd, after the 1 September
 * demo: "a nice little touch" like the loader Stripe uses. June's shape for it:
 * "the blocks like building themselves."
 *
 * Four squares in the arrangement of the AFELL mark, each rising in turn and
 * holding, so the loop reads as assembly. The motion goes under
 * `prefers-reduced-motion`; the shape stays, because the shape is the point.
 *
 * Not a spinner: a spinner says "unknown wait" in a vocabulary every site
 * shares. This says whose site you are waiting on.
 */
export function Loader({ label = "Loading" }: { label?: string }) {
  return (
    <span role="status" className="inline-flex flex-col gap-[3px]">
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className="flex gap-[3px]">
          {[0, 1].map((j) => {
            const block = i * 2 + j;
            // The mark is a square with a notch out of the lower right, so the
            // last block is left out rather than drawn.
            if (block === 7) return <span key={j} className="size-2" />;
            return (
              <span
                key={j}
                className="bg-brand animate-block-build size-2 rounded-[2px]"
                style={{ "--block": block } as React.CSSProperties}
              />
            );
          })}
        </span>
      ))}
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** The same, centred on an otherwise empty screen. */
export function FullScreenLoader({ label = "Loading" }: { label?: string }) {
  return (
    <div className="flex min-h-screen w-full items-center justify-center">
      <Loader label={label} />
    </div>
  );
}

export default Loader;
